import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { NextFitHistoryReport } from "@/components/NextFitHistoryReport";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { TrendingUp, Users as UsersIcon, Wallet, Sparkles, Download, RefreshCw, Loader2, Store, Smartphone, Building2, Zap, ArrowUpRight, Percent, DoorOpen, CreditCard, ClipboardList, ChevronDown, ChevronRight } from "lucide-react";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (d) => new Date(Date.now() - d * 86400e3).toISOString().slice(0, 10);

const SOURCE_COLORS = { "Totem": "#8B5CF6", "Portal do Aluno": "#06B6D4", "Site / Autocadastro": "#E11D48", "Sistema": "#64748b", "Sistema (PDV)": "#0f172a", "Link de Cobrança": "#F59E0B" };
const KIND_COLORS = { pix: "#E11D48", card: "#3B82F6", cash: "#10B981", manual: "#F59E0B", stripe: "#6366F1", other: "#94a3b8" };
const METHOD_LABEL = { cash: "Dinheiro", card: "Cartão", pix: "Pix", manual: "Manual", stripe: "Stripe", other: "Outro", transferencia: "Transferência/Depósito", nextfit: "Next Fit" };

const KpiCard = ({ label, value, sub, icon: Icon, color = "#0f172a", testid }) => (
  <Card className="p-5 border-slate-200" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="font-heading text-3xl font-black mt-1" style={{ color }}>{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + "1a" }}>
        <Icon className="w-5 h-5" style={{ color }}/>
      </div>
    </div>
  </Card>
);

const csvDownload = (rows, headers, filename) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map((h) => h.label).join(","), ...rows.map((r) => headers.map((h) => esc(h.get(r))).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob); link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
};

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("digital");
  const [dfFrom, setDfFrom] = useState(daysAgo(30));
  const [dfTo, setDfTo] = useState(todayStr());
  const [digital, setDigital] = useState(null);
  const [students, setStudents] = useState(null);
  const [general, setGeneral] = useState(null);
  const [groupBy, setGroupBy] = useState("month");
  const [methodsGroupBy, setMethodsGroupBy] = useState("day"); // day | month | year
  const [methods, setMethods] = useState(null);
  const [activePlans, setActivePlans] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    try { const { data } = await api.get("/reports/summary"); setSummary(data); }
    catch (e) { toast.error("Erro ao carregar resumo"); }
  };
  const loadGeneral = async () => {
    setLoading(true);
    try { const { data } = await api.get("/turnstile/general-release/stats?days=90"); setGeneral(data); }
    catch (e) { toast.error("Erro liberação geral"); }
    finally { setLoading(false); }
  };
  const loadDigital = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/digital-sales?date_from=${dfFrom}&date_to=${dfTo}`); setDigital(data); }
    catch (e) { toast.error("Erro relatório digital"); }
    finally { setLoading(false); }
  };
  const loadStudents = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/new-students?date_from=${dfFrom}&date_to=${dfTo}&group_by=${groupBy}`); setStudents(data); }
    catch (e) { toast.error("Erro relatório alunos"); }
    finally { setLoading(false); }
  };
  const loadMethods = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/sales-by-method?date_from=${dfFrom}&date_to=${dfTo}&group_by=${methodsGroupBy}`); setMethods(data); }
    catch (e) { toast.error("Erro vendas por pagamento"); }
    finally { setLoading(false); }
  };
  const loadActivePlans = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reports/active-plans"); setActivePlans(data); setExpandedPlan(null); }
    catch (e) { toast.error("Erro planos ativos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => {
    if (tab === "digital") loadDigital();
    if (tab === "students") loadStudents();
    if (tab === "general") loadGeneral();
    if (tab === "methods") loadMethods();
    if (tab === "plans") loadActivePlans();
    /* eslint-disable-next-line */
  }, [tab, dfFrom, dfTo, groupBy, methodsGroupBy]);

  const digitalPieData = useMemo(() => digital?.by_source?.filter((s) => s.revenue > 0) || [], [digital]);
  const studentsBySourcePie = useMemo(() => students?.by_source?.filter((s) => s.count > 0) || [], [students]);

  return (
    <div className="space-y-5">
      <PageHeader title="Relatórios" subtitle="Vendas digitais, novos alunos e evolução do negócio"/>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Alunos ativos" value={summary.total_students} icon={UsersIcon} color="#0f172a" testid="kpi-total-students"/>
          <KpiCard label="Novos 7d" value={summary.new_students_7d} sub="Últimos 7 dias" icon={ArrowUpRight} color="#10B981" testid="kpi-new-7d"/>
          <KpiCard label="Novos 30d" value={summary.new_students_30d} sub="Últimos 30 dias" icon={UsersIcon} color="#0284c7" testid="kpi-new-30d"/>
          <KpiCard label="Receita 30d" value={money(summary.revenue_30d)} icon={Wallet} color="#0f172a" testid="kpi-rev-total"/>
          <KpiCard label="Vendas digitais" value={money(summary.digital_revenue_30d)} sub={`${summary.digital_pct}% do total`} icon={Smartphone} color="#E11D48" testid="kpi-rev-digital"/>
          <KpiCard label="Vendas sistema" value={money(summary.system_revenue_30d)} sub="Recepção/PDV" icon={Store} color="#8B5CF6" testid="kpi-rev-system"/>
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 border-slate-200 flex flex-wrap items-end gap-2">
        <div><Label className="text-[10px] uppercase tracking-wider">De</Label><Input type="date" value={dfFrom} onChange={(e)=>setDfFrom(e.target.value)} className="h-9" data-testid="reports-date-from"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider">Até</Label><Input type="date" value={dfTo} onChange={(e)=>setDfTo(e.target.value)} className="h-9" data-testid="reports-date-to"/></div>
        <div className="flex gap-1 flex-wrap">
          {[
            { label: "Hoje", from: todayStr(), to: todayStr() },
            { label: "7 dias", from: daysAgo(7), to: todayStr() },
            { label: "30 dias", from: daysAgo(30), to: todayStr() },
            { label: "90 dias", from: daysAgo(90), to: todayStr() },
            { label: "Este ano", from: `${new Date().getFullYear()}-01-01`, to: todayStr() },
          ].map((p) => (
            <Button key={p.label} size="sm" variant="outline" onClick={()=>{setDfFrom(p.from); setDfTo(p.to);}} className="h-9 text-xs" data-testid={`reports-preset-${p.label}`}>{p.label}</Button>
          ))}
        </div>
        <Button size="sm" onClick={()=>{ loadSummary(); if (tab === "digital") loadDigital(); else loadStudents(); }} disabled={loading} className="bg-rose-600 hover:bg-rose-700 h-9" data-testid="reports-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
        </Button>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 max-w-5xl h-auto">
          <TabsTrigger value="digital" data-testid="tab-digital-sales"><Smartphone className="w-4 h-4 mr-1"/>Vendas Digitais</TabsTrigger>
          <TabsTrigger value="methods" data-testid="tab-sales-methods"><CreditCard className="w-4 h-4 mr-1"/>Por Pagamento</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-active-plans"><ClipboardList className="w-4 h-4 mr-1"/>Planos Ativos</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-new-students"><UsersIcon className="w-4 h-4 mr-1"/>Novos Alunos</TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general-release"><DoorOpen className="w-4 h-4 mr-1"/>Liberação Geral</TabsTrigger>
          <TabsTrigger value="nextfit" data-testid="tab-nextfit-history"><Building2 className="w-4 h-4 mr-1"/>Histórico Next Fit</TabsTrigger>
        </TabsList>

        {/* DIGITAL SALES */}
        <TabsContent value="digital" className="space-y-4 mt-4">
          {digital ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <KpiCard label="Receita digital total" value={money(digital.total_revenue)} icon={Sparkles} color="#E11D48" testid="kpi-digital-total"/>
                <KpiCard label="Nº de vendas" value={digital.total_count} icon={Zap} color="#8B5CF6" testid="kpi-digital-count"/>
                <KpiCard label="Ticket médio" value={digital.total_count ? money(digital.total_revenue / digital.total_count) : "—"} icon={Wallet} color="#06B6D4" testid="kpi-digital-avg"/>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Receita por origem</h3>
                  {digitalPieData.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={digitalPieData} dataKey="revenue" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={(e)=>`${e.label}: ${money(e.revenue)}`}>
                          {digitalPieData.map((d) => <Cell key={d.label} fill={SOURCE_COLORS[d.label] || "#64748b"}/>)}
                        </Pie>
                        <Tooltip formatter={(v)=>money(v)}/>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem vendas no período</div>}
                </Card>
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Evolução diária</h3>
                  {digital.daily_series?.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={digital.daily_series}>
                        <CartesianGrid stroke="#f1f5f9"/>
                        <XAxis dataKey="date" tick={{fontSize: 10}}/>
                        <YAxis tick={{fontSize: 10}} tickFormatter={(v)=>`R$${v}`}/>
                        <Tooltip formatter={(v)=>money(v)}/>
                        <Line type="monotone" dataKey="revenue" stroke="#E11D48" strokeWidth={2} dot={{fill: "#E11D48"}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem dados</div>}
                </Card>
              </div>

              <Card className="border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-heading font-bold text-slate-900">Todas as vendas digitais ({digital.sales.length})</h3>
                  <Button size="sm" variant="outline" onClick={()=>csvDownload(digital.sales, [
                    { label: "Data", get: r => new Date(r.created_at).toLocaleString("pt-BR") },
                    { label: "Origem", get: r => r.source_label },
                    { label: "Aluno", get: r => r.student_name },
                    { label: "Plano", get: r => r.plan_name },
                    { label: "Método", get: r => r.method },
                    { label: "Valor", get: r => r.amount.toFixed(2) },
                  ], `vendas-digitais_${dfFrom}_${dfTo}.csv`)} data-testid="reports-digital-csv">
                    <Download className="w-4 h-4 mr-1"/>CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-2">Data</th><th className="px-4 py-2">Origem</th><th className="px-4 py-2">Aluno</th><th className="px-4 py-2">Plano</th><th className="px-4 py-2">Método</th><th className="px-4 py-2 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody data-testid="reports-digital-table">
                      {digital.sales.map((s) => (
                        <tr key={s.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-600">{new Date(s.created_at).toLocaleString("pt-BR")}</td>
                          <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ background: (SOURCE_COLORS[s.source_label] || "#64748b") + "1a", color: SOURCE_COLORS[s.source_label] || "#475569" }}>{s.source_label}</span></td>
                          <td className="px-4 py-2 font-semibold text-slate-800">{s.student_name}</td>
                          <td className="px-4 py-2 text-slate-600">{s.plan_name}</td>
                          <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider" style={{ background: (KIND_COLORS[s.method] || "#94a3b8") + "1a", color: KIND_COLORS[s.method] || "#475569" }}>{s.method}</span></td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-emerald-600">{money(s.amount)}</td>
                        </tr>
                      ))}
                      {digital.sales.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Nenhuma venda digital neste período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400"/></div>
          )}
        </TabsContent>

        {/* SALES BY PAYMENT METHOD */}
        <TabsContent value="methods" className="space-y-4 mt-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Agrupar por:</Label>
            <div className="flex gap-1">
              <Button size="sm" variant={methodsGroupBy === "day" ? "default" : "outline"} onClick={()=>setMethodsGroupBy("day")} className={methodsGroupBy === "day" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="methods-group-day">Dia</Button>
              <Button size="sm" variant={methodsGroupBy === "month" ? "default" : "outline"} onClick={()=>setMethodsGroupBy("month")} className={methodsGroupBy === "month" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="methods-group-month">Mês</Button>
              <Button size="sm" variant={methodsGroupBy === "year" ? "default" : "outline"} onClick={()=>setMethodsGroupBy("year")} className={methodsGroupBy === "year" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="methods-group-year">Ano</Button>
            </div>
          </div>

          {methods ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <KpiCard label="Total no período" value={money(methods.total)} icon={Wallet} color="#E11D48" testid="kpi-methods-total"/>
                <KpiCard label="Nº de vendas" value={methods.count} icon={Zap} color="#8B5CF6" testid="kpi-methods-count"/>
                <KpiCard label="Ticket médio" value={methods.count ? money(methods.total / methods.count) : "—"} icon={TrendingUp} color="#06B6D4" testid="kpi-methods-avg"/>
              </div>

              {/* KPI por método */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="methods-by-method-cards">
                {methods.by_method.map((m) => (
                  <Card key={m.method} className="p-4 border-slate-200">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: KIND_COLORS[m.method] || "#475569" }}>{METHOD_LABEL[m.method] || m.method}</div>
                    <div className="font-heading text-2xl font-black mt-1 text-slate-900">{money(m.total)}</div>
                    <div className="text-xs text-slate-500 mt-1">{m.pct}% do total</div>
                  </Card>
                ))}
                {methods.by_method.length === 0 && <Card className="p-4 border-slate-200 col-span-full text-center text-slate-400 text-sm">Sem vendas no período</Card>}
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Evolução por forma de pagamento ({methodsGroupBy === "day" ? "diária" : methodsGroupBy === "month" ? "mensal" : "anual"})</h3>
                  {methods.series.length ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={methods.series}>
                        <CartesianGrid stroke="#f1f5f9"/>
                        <XAxis dataKey="period" tick={{fontSize: 10}}/>
                        <YAxis tick={{fontSize: 10}} tickFormatter={(v)=>`R$${v}`}/>
                        <Tooltip formatter={(v)=>money(v)}/>
                        <Legend wrapperStyle={{fontSize: 11}}/>
                        <Bar dataKey="cash" stackId="a" fill={KIND_COLORS.cash} name="Dinheiro"/>
                        <Bar dataKey="card" stackId="a" fill={KIND_COLORS.card} name="Cartão"/>
                        <Bar dataKey="pix" stackId="a" fill={KIND_COLORS.pix} name="Pix"/>
                        <Bar dataKey="manual" stackId="a" fill={KIND_COLORS.manual} name="Manual"/>
                        <Bar dataKey="stripe" stackId="a" fill={KIND_COLORS.stripe} name="Stripe"/>
                        <Bar dataKey="other" stackId="a" fill={KIND_COLORS.other} name="Outro"/>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem dados no período</div>}
                </Card>
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Participação por forma de pagamento</h3>
                  {methods.by_method.length ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={methods.by_method} dataKey="total" nameKey="method" cx="50%" cy="50%" outerRadius={100} label={(e)=>`${METHOD_LABEL[e.method] || e.method}: ${money(e.total)}`}>
                          {methods.by_method.map((d) => <Cell key={d.method} fill={KIND_COLORS[d.method] || "#64748b"}/>)}
                        </Pie>
                        <Tooltip formatter={(v)=>money(v)}/>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem vendas no período</div>}
                </Card>
              </div>

              <Card className="border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-heading font-bold text-slate-900">Detalhamento por período ({methods.series.length})</h3>
                  <Button size="sm" variant="outline" onClick={()=>csvDownload(methods.series, [
                    { label: "Período", get: r => r.period },
                    { label: "Dinheiro", get: r => (r.cash || 0).toFixed(2) },
                    { label: "Cartão", get: r => (r.card || 0).toFixed(2) },
                    { label: "Pix", get: r => (r.pix || 0).toFixed(2) },
                    { label: "Manual", get: r => (r.manual || 0).toFixed(2) },
                    { label: "Stripe", get: r => (r.stripe || 0).toFixed(2) },
                    { label: "Outro", get: r => (r.other || 0).toFixed(2) },
                    { label: "Total", get: r => (r.total || 0).toFixed(2) },
                    { label: "Qtd vendas", get: r => r.count },
                  ], `vendas-por-pagamento_${methodsGroupBy}_${dfFrom}_${dfTo}.csv`)} data-testid="methods-csv">
                    <Download className="w-4 h-4 mr-1"/>CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-2">Período</th>
                        <th className="px-4 py-2 text-right">Dinheiro</th>
                        <th className="px-4 py-2 text-right">Cartão</th>
                        <th className="px-4 py-2 text-right">Pix</th>
                        <th className="px-4 py-2 text-right">Manual</th>
                        <th className="px-4 py-2 text-right">Stripe</th>
                        <th className="px-4 py-2 text-right">Outro</th>
                        <th className="px-4 py-2 text-right font-bold">Total</th>
                        <th className="px-4 py-2 text-right">Qtd</th>
                      </tr>
                    </thead>
                    <tbody data-testid="methods-table">
                      {methods.series.map((r) => (
                        <tr key={r.period} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 font-semibold text-slate-800">{r.period}</td>
                          <td className="px-4 py-2 text-right font-mono text-emerald-600">{r.cash ? money(r.cash) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono text-sky-600">{r.card ? money(r.card) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono text-rose-600">{r.pix ? money(r.pix) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono text-amber-600">{r.manual ? money(r.manual) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono text-indigo-600">{r.stripe ? money(r.stripe) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono text-slate-500">{r.other ? money(r.other) : "—"}</td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">{money(r.total)}</td>
                          <td className="px-4 py-2 text-right text-slate-500">{r.count}</td>
                        </tr>
                      ))}
                      {methods.series.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Nenhuma venda registrada no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400"/></div>
          )}
        </TabsContent>

        {/* ACTIVE PLANS */}
        <TabsContent value="plans" className="space-y-4 mt-4">
          {activePlans ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <KpiCard label="Alunos ativos" value={activePlans.total_active} icon={UsersIcon} color="#E11D48" testid="kpi-plans-active"/>
                <KpiCard label="Planos em uso" value={activePlans.plans.length} icon={ClipboardList} color="#8B5CF6" testid="kpi-plans-count"/>
                <KpiCard label="Ativos sem plano" value={activePlans.without_plan} sub="Verifique cadastro" icon={Percent} color="#F59E0B" testid="kpi-plans-without"/>
              </div>

              <Card className="p-5 border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 mb-3">Distribuição de alunos por plano</h3>
                {activePlans.plans.length ? (
                  <ResponsiveContainer width="100%" height={Math.max(220, activePlans.plans.length * 36)}>
                    <BarChart data={activePlans.plans} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid stroke="#f1f5f9"/>
                      <XAxis type="number" tick={{fontSize: 10}} allowDecimals={false}/>
                      <YAxis dataKey="plan_name" type="category" tick={{fontSize: 11}} width={180}/>
                      <Tooltip/>
                      <Bar dataKey="total" fill="#E11D48" radius={[0,6,6,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="py-8 text-center text-slate-400 text-sm">Nenhum aluno ativo com plano</div>}
              </Card>

              <Card className="border-slate-200 overflow-hidden" data-testid="active-plans-list">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-heading font-bold text-slate-900">Planos ativos (clique para ver por unidade)</h3>
                  <Button size="sm" variant="outline" onClick={()=>csvDownload(
                    activePlans.plans.flatMap(p => p.by_unit.map(u => ({ plan: p.plan_name, price: p.price, unit: u.unit_name, count: u.count }))),
                    [
                      { label: "Plano", get: r => r.plan },
                      { label: "Preço", get: r => Number(r.price || 0).toFixed(2) },
                      { label: "Unidade", get: r => r.unit },
                      { label: "Alunos ativos", get: r => r.count },
                    ],
                    `planos-ativos-por-unidade_${todayStr()}.csv`
                  )} data-testid="plans-csv">
                    <Download className="w-4 h-4 mr-1"/>CSV
                  </Button>
                </div>
                <div className="divide-y divide-slate-100">
                  {activePlans.plans.map((p) => {
                    const expanded = expandedPlan === p.plan_id;
                    return (
                      <div key={p.plan_id}>
                        <button
                          onClick={()=>setExpandedPlan(expanded ? null : p.plan_id)}
                          className="w-full flex items-center justify-between px-5 py-3 hover:bg-rose-50 transition text-left"
                          data-testid={`plan-row-${p.plan_id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {expanded ? <ChevronDown className="w-4 h-4 text-rose-600 shrink-0"/> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0"/>}
                            <div className="min-w-0">
                              <div className="font-heading font-bold text-slate-900 truncate">{p.plan_name}</div>
                              <div className="text-xs text-slate-500 truncate">{money(p.price)} · {p.duration_days ? `${p.duration_days} dia(s)` : `${p.duration_months} mês(es)`} · {p.by_unit.length} unidade(s)</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="font-heading text-2xl font-black text-rose-600 tabular-nums">{p.total}</div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">aluno(s)</div>
                          </div>
                        </button>
                        {expanded && (
                          <div className="bg-slate-50 border-t border-slate-100 px-5 py-3" data-testid={`plan-units-${p.plan_id}`}>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Alunos ativos por unidade</div>
                            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                              {p.by_unit.map((u) => {
                                const pct = p.total > 0 ? Math.round((u.count / p.total) * 100) : 0;
                                return (
                                  <div key={u.unit_id} className="bg-white rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5 text-slate-800 font-semibold text-sm truncate"><Building2 className="w-3.5 h-3.5 text-slate-400"/>{u.unit_name}</div>
                                      <div className="text-[10px] text-slate-500 mt-0.5">{pct}% deste plano</div>
                                    </div>
                                    <div className="font-heading text-xl font-black text-slate-900 tabular-nums">{u.count}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {activePlans.plans.length === 0 && <div className="px-5 py-12 text-center text-slate-500">Nenhum plano com alunos ativos</div>}
                </div>
              </Card>
            </>
          ) : (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400"/></div>
          )}
        </TabsContent>

        {/* NEW STUDENTS */}
        <TabsContent value="students" className="space-y-4 mt-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Agrupar por:</Label>
            <div className="flex gap-1">
              <Button size="sm" variant={groupBy === "day" ? "default" : "outline"} onClick={()=>setGroupBy("day")} className={groupBy === "day" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="reports-group-day">Dia</Button>
              <Button size="sm" variant={groupBy === "month" ? "default" : "outline"} onClick={()=>setGroupBy("month")} className={groupBy === "month" ? "bg-rose-600 hover:bg-rose-700" : ""} data-testid="reports-group-month">Mês</Button>
            </div>
          </div>

          {students ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <KpiCard label="Novos alunos no período" value={students.total} icon={UsersIcon} color="#E11D48" testid="kpi-new-total"/>
                <KpiCard label="Unidade top" value={students.by_unit?.[0]?.label || "—"} sub={students.by_unit?.[0] ? `${students.by_unit[0].count} novos` : ""} icon={Building2} color="#0284c7" testid="kpi-top-unit"/>
                <KpiCard label="Origem top" value={students.by_source?.[0]?.label || "—"} sub={students.by_source?.[0] ? `${students.by_source[0].count} novos` : ""} icon={Sparkles} color="#8B5CF6" testid="kpi-top-source"/>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Evolução — {groupBy === "day" ? "Diária" : "Mensal"}</h3>
                  {students.series?.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={students.series}>
                        <CartesianGrid stroke="#f1f5f9"/>
                        <XAxis dataKey="period" tick={{fontSize: 10}}/>
                        <YAxis tick={{fontSize: 10}}/>
                        <Tooltip/>
                        <Bar dataKey="count" fill="#E11D48" radius={[6,6,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem novos alunos</div>}
                </Card>
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Por origem</h3>
                  {studentsBySourcePie.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={studentsBySourcePie} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={(e)=>`${e.label}: ${e.count}`}>
                          {studentsBySourcePie.map((d, i) => <Cell key={d.label} fill={["#E11D48", "#0284c7", "#8B5CF6", "#F59E0B", "#10B981"][i % 5]}/>)}
                        </Pie>
                        <Tooltip/>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem dados</div>}
                </Card>
              </div>

              <Card className="p-5 border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 mb-3">Por unidade</h3>
                {students.by_unit?.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={students.by_unit} layout="vertical">
                      <CartesianGrid stroke="#f1f5f9"/>
                      <XAxis type="number" tick={{fontSize: 10}}/>
                      <YAxis dataKey="label" type="category" tick={{fontSize: 10}} width={140}/>
                      <Tooltip/>
                      <Bar dataKey="count" fill="#8B5CF6" radius={[0,6,6,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="py-8 text-center text-slate-400 text-sm">Sem dados</div>}
              </Card>

              <Card className="border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-heading font-bold text-slate-900">Lista de novos alunos ({students.students.length})</h3>
                  <Button size="sm" variant="outline" onClick={()=>csvDownload(students.students, [
                    { label: "Data", get: r => new Date(r.created_at).toLocaleString("pt-BR") },
                    { label: "Nome", get: r => r.name },
                    { label: "CPF", get: r => r.cpf },
                    { label: "Telefone", get: r => r.phone },
                    { label: "Unidade", get: r => r.unit_name },
                    { label: "Origem", get: r => r.source === "self-signup" ? "Autocadastro" : (r.source === "manual" ? "Recepção" : r.source) },
                  ], `novos-alunos_${dfFrom}_${dfTo}.csv`)} data-testid="reports-students-csv">
                    <Download className="w-4 h-4 mr-1"/>CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-2">Data</th><th className="px-4 py-2">Nome</th><th className="px-4 py-2">CPF</th><th className="px-4 py-2">Unidade</th><th className="px-4 py-2">Origem</th>
                      </tr>
                    </thead>
                    <tbody data-testid="reports-students-table">
                      {students.students.slice(0, 200).map((s) => (
                        <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-600">{new Date(s.created_at).toLocaleDateString("pt-BR")}</td>
                          <td className="px-4 py-2 font-semibold text-slate-800">{s.name}</td>
                          <td className="px-4 py-2 text-slate-600 font-mono text-xs">{s.cpf}</td>
                          <td className="px-4 py-2 text-slate-600">{s.unit_name || "—"}</td>
                          <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider bg-slate-100 text-slate-700">{s.source === "self-signup" ? "Autocadastro" : (s.source === "manual" ? "Recepção" : s.source)}</span></td>
                        </tr>
                      ))}
                      {students.students.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nenhum aluno neste período</td></tr>}
                      {students.students.length > 200 && <tr><td colSpan={5} className="px-4 py-3 text-center text-xs text-slate-400">Mostrando 200 de {students.students.length} — baixe o CSV para ver todos</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400"/></div>
          )}
        </TabsContent>

        {/* GENERAL RELEASE */}
        <TabsContent value="general" className="space-y-4 mt-4">
          {general ? (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <KpiCard label="Total (90d)" value={general.total} icon={DoorOpen} color="#F59E0B" testid="kpi-general-total"/>
                <KpiCard label="Hoje" value={general.daily.filter(d => d.day === todayStr())[0]?.count || 0} icon={Zap} color="#E11D48" testid="kpi-general-today"/>
                <KpiCard label="Este mês" value={general.monthly.filter(m => m.month === todayStr().slice(0,7))[0]?.count || 0} icon={TrendingUp} color="#8B5CF6" testid="kpi-general-month"/>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Diário — últimos 90 dias</h3>
                  {general.daily?.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={general.daily}>
                        <CartesianGrid stroke="#f1f5f9"/>
                        <XAxis dataKey="day" tick={{fontSize: 10}}/>
                        <YAxis tick={{fontSize: 10}} allowDecimals={false}/>
                        <Tooltip/>
                        <Bar dataKey="count" fill="#F59E0B" radius={[6,6,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem liberações registradas</div>}
                </Card>
                <Card className="p-5 border-slate-200">
                  <h3 className="font-heading font-bold text-slate-900 mb-3">Mensal</h3>
                  {general.monthly?.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={general.monthly}>
                        <CartesianGrid stroke="#f1f5f9"/>
                        <XAxis dataKey="month" tick={{fontSize: 10}}/>
                        <YAxis tick={{fontSize: 10}} allowDecimals={false}/>
                        <Tooltip/>
                        <Line type="monotone" dataKey="count" stroke="#F59E0B" strokeWidth={2} dot={{fill: "#F59E0B"}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div className="py-8 text-center text-slate-400 text-sm">Sem dados</div>}
                </Card>
              </div>

              <Card className="border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-heading font-bold text-slate-900">Últimas liberações ({general.recent.length})</h3>
                  <Button size="sm" variant="outline" onClick={()=>csvDownload(general.recent, [
                    { label: "Data", get: r => new Date(r.timestamp).toLocaleString("pt-BR") },
                    { label: "Operador", get: r => r.released_by_name || "—" },
                    { label: "Unidade", get: r => r.unit_id || "—" },
                    { label: "Observação", get: r => r.note || "" },
                  ], `liberacao-geral_${todayStr()}.csv`)} data-testid="reports-general-csv">
                    <Download className="w-4 h-4 mr-1"/>CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-2">Data/Hora</th><th className="px-4 py-2">Operador</th><th className="px-4 py-2">Unidade</th><th className="px-4 py-2">Observação</th>
                      </tr>
                    </thead>
                    <tbody data-testid="reports-general-table">
                      {general.recent.map((l) => (
                        <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-600">{new Date(l.timestamp).toLocaleString("pt-BR")}</td>
                          <td className="px-4 py-2 font-semibold text-slate-800">{l.released_by_name || "—"}</td>
                          <td className="px-4 py-2 text-slate-600">{l.unit_id ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">{String(l.unit_id).slice(0, 8)}</span> : "—"}</td>
                          <td className="px-4 py-2 text-slate-500 italic">{l.note || "—"}</td>
                        </tr>
                      ))}
                      {general.recent.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">Nenhuma liberação geral registrada</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          ) : (
            <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400"/></div>
          )}
        </TabsContent>

        <TabsContent value="nextfit" className="mt-4">
          {tab === "nextfit" && <NextFitHistoryReport dfFrom={dfFrom} dfTo={dfTo}/>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
